import { type FormEvent, type ReactNode, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { mailFailureReason } from '@/shared/emails/mailFailure';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { useIsPlatform } from '@/shared/session/currentUser';
import { formatDate } from '@/shared/utils/format';

import { addressDomainWarning } from './mailboxAddress';
import {
  mailboxChanges,
  type MailboxEditForm,
  mailboxEditForm,
  mailboxNameIsValid,
} from './mailboxEdit';
import { HOSTINET_HOST, looksLikeAddress, shopMailboxPayload } from './mailboxNew';
import { RowMenu } from './RowMenu';
import {
  type ConnectionTest,
  type Mailbox,
  type MailboxProfile,
  type MailboxWrite,
  type MailboxOnConfirm,
  ON_CONFIRM,
  ON_CONFIRM_HINTS,
  ON_CONFIRM_LABELS,
  PROFILE_LABELS,
  PROFILES,
} from './types';
import {
  useAdminMailboxes,
  useCreateMailbox,
  useDeleteMailbox,
  useTestMailbox,
  useTestSavedMailbox,
  useUpdateMailbox,
} from './useAdmin';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * The mailboxes the connector reads. Register one (Paulino's, say) without touching the
 * server: type the server and the login, «Probar conexión», save. The password never
 * comes back from the API; the form only ever sends it.
 *
 * The table first, at full width (seven columns have to fit at 1280 px); the form under
 * it, in two columns on a desktop and one on a phone. A registered mailbox is edited and
 * tested from the «⋯» of its row (contract v10.17), without typing its password again.
 *
 * Two views of the same tab. A superadministrator (Ignira) sees everything: servers,
 * ports, the delivery rules. An administrator of the shop (Víctor, Ernesto) sees what
 * they can decide: the address, its password, the name and «Al confirmar». The server is
 * always Hostinet's and the rules are Ignira's, so showing them only invites a wrong edit.
 */
export function MailboxesTab() {
  const mailboxes = useAdminMailboxes();
  const platform = useIsPlatform();
  return (
    <div className="flex flex-col gap-4">
      {mailboxes.isPending ? <Spinner variant="block" label="Cargando buzones" /> : null}
      {mailboxes.isError ? (
        <ErrorMessage error={mailboxes.error}>No se pudieron cargar los buzones</ErrorMessage>
      ) : null}
      {mailboxes.data ? (
        <>
          {!mailboxes.data.encryption_configured ? (
            <Callout tone="warn" title="Falta la clave de cifrado en el servidor">
              Quien administra la aplicación tiene que ponerla. Hasta entonces los buzones siguen
              gestionándose por configuración
              {mailboxes.data.environment_mailboxes?.length ? (
                <>
                  {' '}
                  (ahora mismo: <strong>{mailboxes.data.environment_mailboxes.join(', ')}</strong>)
                </>
              ) : null}
              .
            </Callout>
          ) : null}
          {mailboxes.data.source === 'environment' && mailboxes.data.encryption_configured ? (
            <Callout tone="info" compact title="El conector lee la configuración del servidor.">
              Ahora mismo lee {mailboxes.data.environment_mailboxes?.join(', ')}. La próxima vez que
              arranque pasan a esta lista y se gestionan desde aquí; para dejar de leerlos del todo
              hay que quitarlos también de la configuración.
            </Callout>
          ) : null}
          <Card
            title="Buzones que lee el sistema"
            description="De cada uno lee el correo que llega, lo clasifica y hace lo que diga «Activación». Dar de baja un buzón para su lectura y borrarlo lo quita de aquí; ninguna de las dos toca el correo."
            padded={false}
          >
            {(mailboxes.data.items ?? []).length === 0 ? (
              <p className="text-ink-3 p-4 text-[14px]">
                Ningún buzón registrado desde aquí todavía.
              </p>
            ) : (
              <MailboxTable items={mailboxes.data.items ?? []} platform={platform} />
            )}
          </Card>
          {platform ? (
            <NewMailboxForm disabled={!mailboxes.data.encryption_configured} />
          ) : (
            <ShopMailboxForm disabled={!mailboxes.data.encryption_configured} />
          )}
        </>
      ) : null}
    </div>
  );
}

function MailboxTable({ items, platform }: { items: Mailbox[]; platform: boolean }) {
  const update = useUpdateMailbox();
  const [deleting, setDeleting] = useState<Mailbox | null>(null);
  const [editing, setEditing] = useState<Mailbox | null>(null);
  const [testing, setTesting] = useState<Mailbox | null>(null);
  const remove = useDeleteMailbox(() => setDeleting(null));
  const tester = useTestSavedMailbox();

  function runTest(mailbox: Mailbox) {
    tester.reset();
    setTesting(mailbox);
    tester.mutate({ id: mailbox.id });
  }

  return (
    <div className="flex flex-col">
      <Table label="Buzones" minWidth="0">
        <THead>
          <Tr>
            <Th>Buzón</Th>
            {platform ? <Th>Servidor</Th> : null}
            {platform ? <Th>Reglas de reparto</Th> : null}
            <Th>Al confirmar</Th>
            <Th>Carpeta de spam</Th>
            <Th>Última prueba</Th>
            <Th>Estado</Th>
            <Th>
              <span className="sr-only">Acciones</span>
            </Th>
          </Tr>
        </THead>
        <TBody>
          {items.map((mailbox) => (
            <Tr key={mailbox.id} className={mailbox.enabled ? undefined : 'text-ink-3'}>
              <Td className="align-middle">
                <span className="text-ink font-medium">{mailbox.name}</span>
              </Td>
              {platform ? (
                <Td className="align-middle">
                  <span className="block leading-5">
                    {mailbox.host}:{mailbox.port}
                  </span>
                  {mailbox.smtp_host ? (
                    <span className="text-ink-3 block text-[12.5px] leading-4">
                      salida {mailbox.smtp_host}:{mailbox.smtp_port}
                    </span>
                  ) : null}
                </Td>
              ) : null}
              {platform ? (
                <Td className="align-middle">{PROFILE_LABELS[mailbox.profile]}</Td>
              ) : null}
              <Td className="align-middle">{ON_CONFIRM_LABELS[mailbox.on_confirm ?? 'move']}</Td>
              <Td className="align-middle">{mailbox.spam_folder ?? ''}</Td>
              <Td className="align-middle whitespace-nowrap tabular-nums">
                {mailbox.tested_at ? formatDate(dateFormat, mailbox.tested_at) : ''}
              </Td>
              <Td className="align-middle">
                <span className="flex flex-wrap gap-1">
                  {mailbox.enabled ? null : <Badge tone="neutral">De baja</Badge>}
                  {mailbox.last_error ? (
                    <Badge
                      tone="danger"
                      title={platform ? mailbox.last_error : mailFailureReason(mailbox.last_error)}
                    >
                      último error
                    </Badge>
                  ) : null}
                </span>
              </Td>
              <Td className="text-right align-middle">
                <RowMenu
                  label={`Acciones de ${mailbox.name}`}
                  items={[
                    { label: 'Editar', onSelect: () => setEditing(mailbox) },
                    {
                      label: 'Probar conexión',
                      disabled: tester.isPending,
                      onSelect: () => runTest(mailbox),
                    },
                    {
                      label: mailbox.enabled ? 'Dar de baja' : 'Volver a leer',
                      tone: mailbox.enabled ? 'danger' : 'default',
                      disabled: update.isPending,
                      onSelect: () =>
                        update.mutate({ id: mailbox.id, payload: { enabled: !mailbox.enabled } }),
                    },
                    {
                      label: 'Borrar definitivamente',
                      tone: 'danger',
                      disabled: update.isPending || remove.isPending,
                      onSelect: () => {
                        remove.reset();
                        setDeleting(mailbox);
                      },
                    },
                  ]}
                />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {update.isError ? (
        <ErrorMessage error={update.error} className="m-4">
          No se pudo guardar el buzón
        </ErrorMessage>
      ) : null}
      {deleting ? (
        <DeleteMailboxDialog
          mailbox={deleting}
          busy={remove.isPending}
          error={remove.isError ? remove.error : null}
          onConfirm={() => remove.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
      {editing ? (
        <EditMailboxDialog mailbox={editing} platform={platform} onClose={() => setEditing(null)} />
      ) : null}
      {testing ? (
        <Dialog
          title="Probar conexión"
          description={testing.name}
          size="sm"
          onClose={() => setTesting(null)}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => runTest(testing)}
                disabled={tester.isPending}
              >
                Probar otra vez
              </Button>
              <Button onClick={() => setTesting(null)} data-autofocus>
                Entendido
              </Button>
            </>
          }
        >
          {tester.isPending ? (
            <Spinner
              label={platform ? `Entrando en ${testing.host} con lo guardado` : 'Probando…'}
            />
          ) : null}
          {tester.data ? <ConnectionResult result={tester.data} /> : null}
          {tester.isError ? <ErrorMessage error={tester.error} /> : null}
        </Dialog>
      ) : null}
    </div>
  );
}

/** What the server said to «Probar conexión», for a new mailbox and for a saved one. */
function ConnectionResult({ result }: { result: ConnectionTest }) {
  const works = result.imap_ok && result.smtp_ok;
  return (
    <Callout
      tone={works ? 'ok' : 'danger'}
      title={works ? 'La conexión funciona' : 'El servidor ha dicho que no'}
    >
      {result.error ? (
        <>
          <p>{mailFailureReason(result.error)}.</p>
          <TechnicalDetails text={result.error} />
        </>
      ) : null}
      {result.folders?.length ? (
        <p className="mt-1 text-[13px]">Carpetas: {result.folders.join(', ')}</p>
      ) : null}
      {result.spam_folder ? (
        <p className="text-[13px]">Carpeta de spam: {result.spam_folder}</p>
      ) : null}
      {result.authserv_id ? (
        <p className="text-[13px]">Firma del servidor de entrada: {result.authserv_id}</p>
      ) : null}
      {result.imap_ok ? (
        <p className="text-[13px]">
          Papelera: {result.trash_folder ?? 'no la encuentra («Eliminar» no podrá usarse)'}
          {result.sent_folder ? ` · Enviados: ${result.sent_folder}` : ''}
        </p>
      ) : null}
    </Callout>
  );
}

/**
 * «Editar» of a registered mailbox. The address is what the mail it brought in is filed
 * under, so it does not change here: for another one, register it. The password starts
 * empty and stays so unless it is being replaced. «Probar conexión» tests what is typed
 * over what is stored and saves nothing; «Guardar cambios» sends only what changed.
 *
 * An administrator of the shop sees the name, «Al confirmar» and the password: the user
 * is the address, the server is Hostinet's and the rules are a superadministrator's.
 */
function EditMailboxDialog({
  mailbox,
  platform,
  onClose,
}: {
  mailbox: Mailbox;
  platform: boolean;
  onClose: () => void;
}) {
  const [form, setForm] = useState<MailboxEditForm>(() => mailboxEditForm(mailbox));
  const [result, setResult] = useState<ConnectionTest | null>(null);
  const update = useUpdateMailbox();
  const tester = useTestSavedMailbox();
  const changes = mailboxChanges(mailbox, form);
  const nameValid = mailboxNameIsValid(form.name);
  const valid = nameValid && form.port >= 1 && form.smtp_port >= 1;
  const busy = update.isPending || tester.isPending;

  function set<K extends keyof MailboxEditForm>(key: K, value: MailboxEditForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setResult(null);
  }

  function runTest() {
    tester.mutate(
      { id: mailbox.id, typed: changes },
      {
        onSuccess: (outcome) => {
          setResult(outcome);
          // What the server said, into the empty fields, as the new-mailbox form does.
          setForm((current) => ({
            ...current,
            spam_folder: current.spam_folder.trim() || (outcome.spam_folder ?? ''),
            authserv_id: current.authserv_id.trim() || (outcome.authserv_id ?? ''),
          }));
        },
      },
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    update.mutate({ id: mailbox.id, payload: changes }, { onSuccess: onClose });
  }

  const formId = `editar-buzon-${mailbox.id}`;
  return (
    <Dialog
      title="Editar buzón"
      description={mailbox.name}
      size="lg"
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="secondary" onClick={runTest} disabled={busy || !valid}>
            {tester.isPending ? 'Probando…' : 'Probar conexión'}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={busy || !valid || Object.keys(changes).length === 0}
          >
            {update.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={submit}
        aria-label={`Editar ${mailbox.name}`}
        className="flex flex-col gap-4"
      >
        <p className="text-ink-3 text-[13.5px]">
          {platform
            ? 'Si cambias el servidor, el usuario o la contraseña, se prueba la conexión antes de guardar y, si no funciona, no se guarda nada.'
            : 'Si cambias la contraseña, se prueba antes de guardar y, si no funciona, no se guarda nada.'}
        </p>
        <Group title="El buzón">
          <Input
            label="Nombre del buzón"
            type="email"
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            autoComplete="off"
            hint={`Solo el nombre con el que sale en la Bandeja, en «Activación» y en los buzones de cada persona. Cambiarlo no toca el servidor, el usuario ni la contraseña, el correo ya guardado pasa al nombre nuevo, y los reenvíos siguen saliendo desde ${mailbox.address}.`}
            error={nameValid ? undefined : 'Tiene que tener forma de dirección: algo@dominio.'}
            required
          />
          {platform ? (
            <Select
              label="Reglas de reparto"
              value={form.profile}
              onChange={(event) => set('profile', event.target.value as MailboxProfile)}
            >
              {PROFILES.map((profile) => (
                <option key={profile} value={profile}>
                  {PROFILE_LABELS[profile]}
                </option>
              ))}
            </Select>
          ) : null}
          <Select
            label="Al confirmar"
            value={form.on_confirm}
            hint={ON_CONFIRM_HINTS[form.on_confirm]}
            onChange={(event) => set('on_confirm', event.target.value as MailboxOnConfirm)}
          >
            {ON_CONFIRM.map((each) => (
              <option key={each} value={each}>
                {ON_CONFIRM_LABELS[each]}
              </option>
            ))}
          </Select>
          {platform ? (
            <Input
              label="Usuario"
              value={form.username}
              onChange={(event) => set('username', event.target.value)}
              autoComplete="off"
              required
            />
          ) : null}
          <Input
            label="Contraseña nueva"
            type="password"
            optional
            value={form.password}
            onChange={(event) => set('password', event.target.value)}
            autoComplete="new-password"
            hint={
              platform
                ? 'Vacía, se queda la que hay.'
                : `Solo si ha cambiado la de ${mailbox.username}. Vacía, se queda la que hay.`
            }
          />
        </Group>
        {platform ? (
          <>
            <Group title="Servidor de correo" hint="Entrada para leer, salida para reenviar.">
              <Input
                label="Servidor de entrada (IMAP)"
                value={form.host}
                onChange={(event) => set('host', event.target.value)}
                required
              />
              <Input
                label="Puerto de entrada"
                type="number"
                value={form.port}
                onChange={(event) => set('port', Number(event.target.value))}
              />
              <Input
                label="Servidor de salida (SMTP)"
                optional
                value={form.smtp_host}
                onChange={(event) => set('smtp_host', event.target.value)}
              />
              <Input
                label="Puerto de salida"
                type="number"
                value={form.smtp_port}
                onChange={(event) => set('smtp_port', Number(event.target.value))}
              />
            </Group>
            <Group title="Se rellenan solos al probar la conexión">
              <Input
                label="Carpeta de spam"
                optional
                value={form.spam_folder}
                onChange={(event) => set('spam_folder', event.target.value)}
                hint="Vacía, el sistema la busca en el servidor."
              />
              <Input
                label="Firma del servidor de entrada"
                optional
                value={form.authserv_id}
                onChange={(event) => set('authserv_id', event.target.value)}
                hint="Sin ella no se reenvía nada."
              />
            </Group>
          </>
        ) : null}
        {result ? <ConnectionResult result={result} /> : null}
        {tester.isError ? <ErrorMessage error={tester.error} /> : null}
        {update.isError ? (
          <ErrorMessage error={update.error}>No se pudo guardar el buzón</ErrorMessage>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * «Borrar definitivamente» asks once. What goes and what stays is said in full, because
 * the word «borrar» next to a mailbox reads as «borrar el correo» to anybody who is not
 * the developer: the row and its password go, the mail already brought in stays.
 */
function DeleteMailboxDialog({
  mailbox,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  mailbox: Mailbox;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title="¿Borrar este buzón definitivamente?"
      size="sm"
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Borrando…' : 'Borrar definitivamente'}
          </Button>
        </>
      }
    >
      <p className="text-ink-2 text-[14.5px] leading-relaxed">
        <strong className="text-ink">{mailbox.name}</strong> desaparece de esta lista con su
        contraseña y sus ajustes de «Activación», y el sistema deja de leerlo. El correo que ya
        trajo se queda donde está. Para volver a leerlo habrá que registrarlo de nuevo.
      </p>
      {error ? <ErrorMessage error={error} /> : null}
    </Dialog>
  );
}

/** A titled block of fields, two columns on a desktop. */
function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-ink text-[14px] font-semibold">{title}</p>
        {hint ? <p className="text-ink-3 text-[12.5px]">{hint}</p> : null}
      </div>
      <div className="grid gap-3 md:grid-cols-2">{children}</div>
    </div>
  );
}

// A new mailbox starts with Hostinet both ways, and a superadministrator who connects one
// elsewhere writes over it.
const EMPTY: MailboxWrite = {
  name: '',
  host: HOSTINET_HOST,
  port: 993,
  ssl: true,
  username: '',
  password: '',
  folder: 'INBOX',
  smtp_host: HOSTINET_HOST,
  smtp_port: 587,
  smtp_starttls: true,
  profile: 'victor',
  enabled: true,
};

function NewMailboxForm({ disabled }: { disabled: boolean }) {
  const [form, setForm] = useState<MailboxWrite>(EMPTY);
  const [result, setResult] = useState<ConnectionTest | null>(null);
  const test = useTestMailbox();
  const create = useCreateMailbox(() => {
    setForm(EMPTY);
    setResult(null);
  });

  function set<K extends keyof MailboxWrite>(key: K, value: MailboxWrite[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setResult(null);
  }

  function credentials(): MailboxWrite {
    return {
      ...form,
      smtp_host: form.smtp_host?.trim() ? form.smtp_host.trim() : null,
      smtp_username: form.smtp_username?.trim() ? form.smtp_username.trim() : null,
      smtp_password: form.smtp_password ? form.smtp_password : null,
      spam_folder: form.spam_folder?.trim() ? form.spam_folder.trim() : null,
      authserv_id: form.authserv_id?.trim() ? form.authserv_id.trim() : null,
      address: form.address?.trim() ? form.address.trim() : null,
    };
  }

  function runTest() {
    test.mutate(credentials(), {
      onSuccess: (outcome) => {
        setResult(outcome);
        // What the server said, into the form, so the person does not have to guess.
        setForm((current) => ({
          ...current,
          spam_folder: current.spam_folder?.trim()
            ? current.spam_folder
            : (outcome.spam_folder ?? current.spam_folder ?? null),
          authserv_id: current.authserv_id?.trim()
            ? current.authserv_id
            : (outcome.authserv_id ?? current.authserv_id ?? null),
        }));
      },
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(credentials());
  }

  return (
    <Card
      title="Conectar un buzón nuevo"
      description="Los mismos datos que se ponen para leer ese correo en el móvil o en Outlook: los da el proveedor de correo. Rellena, pulsa «Probar conexión» y, si funciona, «Guardar buzón»."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <fieldset disabled={disabled} className="flex flex-col gap-4">
          <Group title="El buzón">
            <Input
              label="Dirección del buzón"
              type="email"
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              hint="La que aparece en la bandeja: compras@ferreteriacubas.com"
              error={addressDomainWarning(form.name, form.username) ?? undefined}
              required
            />
            <Select
              label="Reglas de reparto"
              value={form.profile ?? 'victor'}
              onChange={(event) => set('profile', event.target.value as MailboxProfile)}
              hint="A quién se reenvía cada tipo de correo de este buzón."
            >
              {PROFILES.map((profile) => (
                <option key={profile} value={profile}>
                  {PROFILE_LABELS[profile]}
                </option>
              ))}
            </Select>
            <Input
              label="Usuario"
              value={form.username}
              onChange={(event) => set('username', event.target.value)}
              autoComplete="off"
              hint="Casi siempre, la propia dirección."
              required
            />
            <Input
              label="Contraseña"
              type="password"
              value={form.password}
              onChange={(event) => set('password', event.target.value)}
              autoComplete="new-password"
              hint="Se guarda cifrada y no se vuelve a enseñar."
              required
            />
          </Group>
          <Group title="Servidor de correo" hint="Entrada para leer, salida para reenviar.">
            <Input
              label="Servidor de entrada (IMAP)"
              value={form.host}
              onChange={(event) => set('host', event.target.value)}
              hint="Viene puesto el de Hostinet; cámbialo si el buzón es de otro proveedor."
              required
            />
            <Input
              label="Puerto de entrada"
              type="number"
              value={form.port ?? 993}
              onChange={(event) => set('port', Number(event.target.value))}
              hint="993, lo normal."
            />
            <Input
              label="Servidor de salida (SMTP)"
              value={form.smtp_host ?? ''}
              onChange={(event) => set('smtp_host', event.target.value)}
              hint="Por donde saldrán los reenvíos, con el mismo usuario y contraseña. Viene puesto el de Hostinet; cámbialo si el buzón es de otro proveedor."
            />
            <Input
              label="Puerto de salida"
              type="number"
              value={form.smtp_port ?? 587}
              onChange={(event) => set('smtp_port', Number(event.target.value))}
              hint="587, lo normal; 465 en algunos proveedores."
            />
          </Group>
          <details>
            <summary className="text-ink-2 cursor-pointer text-[13.5px] font-medium select-none">
              Se rellenan solos al probar la conexión
            </summary>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Input
                label="Carpeta de spam"
                optional
                value={form.spam_folder ?? ''}
                onChange={(event) => set('spam_folder', event.target.value)}
                hint="Solo si el servidor no la dice al probar."
              />
              <Input
                label="Firma del servidor de entrada"
                optional
                value={form.authserv_id ?? ''}
                onChange={(event) => set('authserv_id', event.target.value)}
                hint="Con ella el sistema sabe que un remitente es quien dice ser. Sin ella no se reenvía nada."
              />
            </div>
          </details>
        </fieldset>

        {result ? <ConnectionResult result={result} /> : null}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={runTest}
            disabled={disabled || test.isPending || !form.host || !form.username || !form.password}
          >
            {test.isPending ? 'Probando…' : 'Probar conexión'}
          </Button>
          <Button type="submit" disabled={disabled || create.isPending || !form.name}>
            {create.isPending ? 'Guardando…' : 'Guardar buzón'}
          </Button>
        </div>
        {test.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(test.error)}
          </p>
        ) : null}
        {create.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(create.error)}
          </p>
        ) : null}
      </form>
    </Card>
  );
}

/**
 * «Conectar un buzón nuevo» for an administrator of the shop: the address and its
 * password. The address is also the login, the server is Hostinet's and the rules are
 * left to a superadministrator (`shopMailboxPayload`). «Probar conexión» and «Guardar
 * buzón» as in the full form: the back tests again before saving and saves nothing that
 * does not connect.
 */
function ShopMailboxForm({ disabled }: { disabled: boolean }) {
  const [address, setAddress] = useState('');
  const [password, setPassword] = useState('');
  const [left, setLeft] = useState(false);
  const [result, setResult] = useState<ConnectionTest | null>(null);
  const test = useTestMailbox();
  const create = useCreateMailbox(() => {
    setAddress('');
    setPassword('');
    setLeft(false);
    setResult(null);
  });
  const addressValid = looksLikeAddress(address);
  const ready = addressValid && password.length > 0;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(shopMailboxPayload(address, password));
  }

  return (
    <Card
      title="Conectar un buzón nuevo"
      description="Escribe la dirección de correo y su contraseña, pulsa «Probar conexión» y, si funciona, «Guardar buzón». Lo demás ya viene puesto."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" aria-label="Conectar un buzón nuevo">
        <fieldset disabled={disabled} className="grid gap-3 md:grid-cols-2">
          <Input
            label="Dirección de correo"
            type="email"
            value={address}
            onChange={(event) => {
              setAddress(event.target.value);
              setResult(null);
            }}
            onBlur={() => setLeft(true)}
            autoComplete="off"
            hint="Entera, por ejemplo compras@ferreteriacubas.com. Es también el usuario con el que se entra en ese correo."
            error={
              left && address.trim() && !addressValid
                ? 'Escribe la dirección entera, por ejemplo compras@ferreteriacubas.com.'
                : undefined
            }
            required
          />
          <Input
            label="Contraseña"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setResult(null);
            }}
            autoComplete="new-password"
            hint="La de ese correo, la misma que se pone en el móvil o en Outlook. Se guarda cifrada y no se vuelve a enseñar."
            required
          />
        </fieldset>

        {result ? <ConnectionResult result={result} /> : null}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              test.mutate(shopMailboxPayload(address, password), { onSuccess: setResult })
            }
            disabled={disabled || test.isPending || !ready}
          >
            {test.isPending ? 'Probando…' : 'Probar conexión'}
          </Button>
          <Button type="submit" disabled={disabled || create.isPending || !ready}>
            {create.isPending ? 'Guardando…' : 'Guardar buzón'}
          </Button>
        </div>
        {test.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(test.error)}
          </p>
        ) : null}
        {create.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(create.error)}
          </p>
        ) : null}
      </form>
    </Card>
  );
}
